import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import BlogCard from './BlogCard';
import blogService from '../../services/blogService';
import { useAnalytics, usePageTracking } from '../../hooks/useAnalytics';

const BlogList = () => {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const blogsPerPage = 6;
  const { trackSearch } = useAnalytics();
  
  // Track page view for blog list
  usePageTracking();

  useEffect(() => {
    loadBlogs();
  }, []);

  const loadBlogs = async () => {
    try {
      setLoading(true);
      const result = await blogService.getPublishedBlogs();
      
      if (result.success) {
        setBlogs(result.data);
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError('Failed to load blogs');
      console.error('Error loading blogs:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async (term) => {
    setSearchTerm(term);
    setCurrentPage(1);
    
    if (!term.trim()) {
      loadBlogs();
      return;
    }

    try {
      setLoading(true);
      const result = await blogService.searchBlogs(term, { isPublished: true });
      
      if (result.success) {
        setBlogs(result.data);
        // Track search analytics
        trackSearch(term, result.data.length);
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError('Failed to search blogs');
      console.error('Error searching blogs:', err);
    } finally {
      setLoading(false);
    }
  };

  // Pagination logic
  const indexOfLastBlog = currentPage * blogsPerPage;
  const indexOfFirstBlog = indexOfLastBlog - blogsPerPage;
  const currentBlogs = blogs.slice(indexOfFirstBlog, indexOfLastBlog);
  const totalPages = Math.ceil(blogs.length / blogsPerPage);

  const paginate = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (loading) {
    return (
      <div className="container py-5">
        <div className="row justify-content-center">
          <div className="col-12 text-center">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-3">Loading blogs...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container py-5">
        <div className="row justify-content-center">
          <div className="col-md-6 text-center">
            <div className="alert alert-danger" role="alert">
              <i className="bi bi-exclamation-triangle-fill me-2"></i>
              {error}
            </div>
            <button 
              className="btn btn-primary" 
              onClick={loadBlogs}
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container py-5" style={{ paddingTop: '120px' }}>
      {/* Breadcrumb */}
      <nav aria-label="breadcrumb" className="mb-4" style={{ paddingTop: '61px' }}>
        <ol className="breadcrumb">
          <li className="breadcrumb-item">
            <Link to="/" className="text-decoration-none">Home</Link>
          </li>
          <li className="breadcrumb-item active" aria-current="page">Blog</li>
        </ol>
      </nav>
      
      {/* Header Section */}
      <div className="row mb-5">
        <div className="col-12 text-center">
          <h1 className="display-4 fw-bold mb-3" style={{paddingTop: '80px'}}>Our Blog</h1>
          <p className="lead text-muted mb-4">
            Insights, tips, and updates from our team
          </p>
          
          {/* Search Bar */}
          <div className="row justify-content-center">
            <div className="col-md-6">
              <div className="input-group">
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search blogs..."
                  value={searchTerm}
                  onChange={(e) => handleSearch(e.target.value)}
                />
                <button className="btn btn-outline-secondary" type="button">
                  <i className="bi bi-search"></i>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Blog Grid */}
      {currentBlogs.length > 0 ? (
        <>
          <div className="row g-4">
            {currentBlogs.map((blog) => (
              <div key={blog.id} className="col-lg-4 col-md-6">
                <BlogCard blog={blog} />
              </div>
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="row mt-5">
              <div className="col-12">
                <nav aria-label="Blog pagination">
                  <ul className="pagination justify-content-center">
                    <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                      <button
                        className="page-link"
                        onClick={() => paginate(currentPage - 1)}
                        disabled={currentPage === 1}
                      >
                        <i className="bi bi-chevron-left"></i>
                      </button>
                    </li>
                    
                    {[...Array(totalPages)].map((_, index) => {
                      const pageNumber = index + 1;
                      return (
                        <li 
                          key={pageNumber} 
                          className={`page-item ${currentPage === pageNumber ? 'active' : ''}`}
                        >
                          <button
                            className="page-link"
                            onClick={() => paginate(pageNumber)}
                          >
                            {pageNumber}
                          </button>
                        </li>
                      );
                    })}
                    
                    <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                      <button
                        className="page-link"
                        onClick={() => paginate(currentPage + 1)}
                        disabled={currentPage === totalPages}
                      >
                        <i className="bi bi-chevron-right"></i>
                      </button>
                    </li>
                  </ul>
                </nav>
              </div>
            </div>
          )}
        </>
      ) : (
        /* Empty State */
        <div className="row justify-content-center">
          <div className="col-md-6 text-center">
            <div className="py-5">
              <i className="bi bi-journal-text display-1 text-muted mb-3"></i>
              <h3 className="mb-3">No blogs found</h3>
              <p className="text-muted mb-4">
                {searchTerm 
                  ? `No blogs match your search for "${searchTerm}"`
                  : "We haven't published any blogs yet. Check back soon!"
                }
              </p>
              {searchTerm && (
                <button 
                  className="btn btn-primary"
                  onClick={() => handleSearch('')}
                >
                  Clear Search
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BlogList;